import type { TransitionType } from '@ve/core';

/**
 * All colours flowing through the compositor are premultiplied alpha in the working space (BT.709
 * primaries, 1.0 = SDR white): linear light, or sRGB for projects that blend in gamma space
 * (`uSrgbBlending`). Layers are converted to the working space when drawn, and a final output pass
 * encodes the result to sRGB. Layer vertices are given in project pixel space and projected to clip
 * space in the vertex shader, so the same draw calls work for any drawing-buffer size (preview
 * resolution scaling, export resizing, framebuffers).
 */
export const LAYER_VS = `#version 300 es
in vec2 aPos;
in vec2 aUv;
uniform vec2 uResolution;
out vec2 vUv;
void main() {
  vec2 clip = (aPos / uResolution) * 2.0 - 1.0;
  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
  vUv = aUv;
}`;

/** Values of the layer shader's `uTransfer`: how a texture's colours are encoded. */
export const TRANSFER_IDS = { srgb: 0, pq: 1, hlg: 2, working: 3 } as const;

/**
 * HDR to SDR: BT.2100 signal to linear BT.709, with HDR reference white (203 cd/m², BT.2408) at SDR
 * white. Highlights above the knee roll off smoothly (extended Reinhard on the max channel, which
 * keeps hue) and reach SDR white at a 1000 cd/m² peak.
 */
const COLOR_GLSL = `
const float REF_WHITE_NITS = 203.0;
const float PEAK = 1000.0 / REF_WHITE_NITS;
const float KNEE = 0.75;
// Columns of the BT.2020 -> BT.709 primaries matrix (linear light).
const mat3 BT2020_TO_BT709 = mat3(
  1.6605, -0.1246, -0.0182,
  -0.5876, 1.1329, -0.1006,
  -0.0728, -0.0083, 1.1187);

vec3 srgbToLinear(vec3 c) {
  return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(0.04045, c));
}

vec3 linearToSrgb(vec3 c) {
  c = clamp(c, 0.0, 1.0);
  return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
}

// SMPTE ST 2084 EOTF: signal -> cd/m².
vec3 pqToNits(vec3 e) {
  const float m1 = 0.1593017578125, m2 = 78.84375;
  const float c1 = 0.8359375, c2 = 18.8515625, c3 = 18.6875;
  vec3 p = pow(clamp(e, 0.0, 1.0), vec3(1.0 / m2));
  return 10000.0 * pow(max(p - c1, 0.0) / (c2 - c3 * p), vec3(1.0 / m1));
}

// BT.2100 HLG: inverse OETF, then the OOTF of a 1000 cd/m² display (system gamma 1.2).
vec3 hlgToNits(vec3 e) {
  const float a = 0.17883277, b = 0.28466892, c = 0.55991073;
  e = clamp(e, 0.0, 1.0);
  vec3 scene = mix(e * e / 3.0, (exp((e - c) / a) + b) / 12.0, step(0.5, e));
  float ys = dot(scene, vec3(0.2627, 0.6780, 0.0593));
  return 1000.0 * pow(ys, 0.2) * scene;
}

vec3 toneMap(vec3 c) {
  c = max(c, 0.0);
  float m = max(c.r, max(c.g, c.b));
  if (m <= KNEE) return c;
  float x = (m - KNEE) / (1.0 - KNEE);
  float w = (PEAK - KNEE) / (1.0 - KNEE);
  float y = min(1.0, x * (1.0 + x / (w * w)) / (1.0 + x));
  return c * ((KNEE + (1.0 - KNEE) * y) / m);
}

vec3 decodeToLinear(vec3 c, int transfer) {
  if (transfer == ${TRANSFER_IDS.pq}) return toneMap(BT2020_TO_BT709 * (pqToNits(c) / REF_WHITE_NITS));
  if (transfer == ${TRANSFER_IDS.hlg}) return toneMap(BT2020_TO_BT709 * (hlgToNits(c) / REF_WHITE_NITS));
  return srgbToLinear(c);
}`;

export const LAYER_FS = `#version 300 es
precision highp float;
in vec2 vUv;
uniform sampler2D uTex;
uniform float uOpacity;
uniform bool uFlipY;
uniform bool uSolid;
uniform vec4 uColor;
uniform int uTransfer;
uniform bool uSrgbBlending;
out vec4 outColor;
${COLOR_GLSL}
void main() {
  if (uSolid) {
    outColor = uColor * uOpacity;
    return;
  }
  vec2 uv = uFlipY ? vec2(vUv.x, 1.0 - vUv.y) : vUv;
  vec4 c = texture(uTex, uv);
  bool inWorkingSpace = uTransfer == ${TRANSFER_IDS.working} || (uSrgbBlending && uTransfer == ${TRANSFER_IDS.srgb});
  if (!inWorkingSpace) {
    vec3 rgb = decodeToLinear(c.a > 0.0 ? c.rgb / c.a : vec3(0.0), uTransfer);
    c.rgb = (uSrgbBlending ? linearToSrgb(rgb) : rgb) * c.a;
  }
  outColor = c * uOpacity;
}`;

/** Full-screen quad; `vUv` has a top-left origin. */
export const FULLSCREEN_VS = `#version 300 es
in vec2 aPos;
out vec2 vUv;
void main() {
  vUv = vec2(aPos.x * 0.5 + 0.5, 0.5 - aPos.y * 0.5);
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

export const TRANSITION_IDS: Readonly<Record<TransitionType, number>> = {
  crossfade: 0,
  dipToBlack: 1,
  dipToWhite: 2,
  wipeLeft: 3,
  wipeRight: 4,
  wipeUp: 5,
  wipeDown: 6,
  slideLeft: 7,
  slideRight: 8,
  zoom: 9,
  circle: 10,
};

/** Inputs are framebuffer textures (bottom-left origin), hence the flip in `sampleA/B`. */
export const TRANSITION_FS = `#version 300 es
precision highp float;
in vec2 vUv;
uniform sampler2D uFrom;
uniform sampler2D uTo;
uniform float uProgress;
uniform int uType;
uniform float uAspect;
out vec4 outColor;

vec4 sampleTex(sampler2D t, vec2 uv) {
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) return vec4(0.0);
  return texture(t, vec2(uv.x, 1.0 - uv.y));
}

const float FEATHER = 0.004;

void main() {
  float p = clamp(uProgress, 0.0, 1.0);
  vec4 a = sampleTex(uFrom, vUv);
  vec4 b = sampleTex(uTo, vUv);
  if (uType == 0) {
    outColor = mix(a, b, p);
  } else if (uType == 1 || uType == 2) {
    vec4 c = uType == 1 ? vec4(0.0, 0.0, 0.0, 1.0) : vec4(1.0);
    outColor = p < 0.5 ? mix(a, c, p * 2.0) : mix(c, b, p * 2.0 - 1.0);
  } else if (uType == 3) {
    outColor = mix(a, b, smoothstep(1.0 - p - FEATHER, 1.0 - p + FEATHER, vUv.x));
  } else if (uType == 4) {
    outColor = mix(b, a, smoothstep(p - FEATHER, p + FEATHER, vUv.x));
  } else if (uType == 5) {
    outColor = mix(a, b, smoothstep(1.0 - p - FEATHER, 1.0 - p + FEATHER, vUv.y));
  } else if (uType == 6) {
    outColor = mix(b, a, smoothstep(p - FEATHER, p + FEATHER, vUv.y));
  } else if (uType == 7) {
    outColor = vUv.x < 1.0 - p ? sampleTex(uFrom, vUv + vec2(p, 0.0)) : sampleTex(uTo, vUv - vec2(1.0 - p, 0.0));
  } else if (uType == 8) {
    outColor = vUv.x > p ? sampleTex(uFrom, vUv - vec2(p, 0.0)) : sampleTex(uTo, vUv + vec2(1.0 - p, 0.0));
  } else if (uType == 9) {
    vec2 za = (vUv - 0.5) / (1.0 + p) + 0.5;
    vec2 zb = (vUv - 0.5) / (0.5 + 0.5 * p) + 0.5;
    outColor = mix(sampleTex(uFrom, za), sampleTex(uTo, zb), p);
  } else {
    vec2 d = (vUv - 0.5) * vec2(uAspect, 1.0);
    float maxR = length(vec2(uAspect, 1.0) * 0.5);
    float r = p * maxR;
    outColor = mix(b, a, smoothstep(r - FEATHER * 4.0, r + FEATHER * 4.0, length(d)));
  }
}`;

/** Encodes the scene (a framebuffer texture, bottom-left origin) for display. */
export const OUTPUT_FS = `#version 300 es
precision highp float;
in vec2 vUv;
uniform sampler2D uScene;
uniform bool uSrgbBlending;
out vec4 outColor;
${COLOR_GLSL}
void main() {
  vec4 c = texture(uScene, vec2(vUv.x, 1.0 - vUv.y));
  outColor = vec4(uSrgbBlending ? clamp(c.rgb, 0.0, 1.0) : linearToSrgb(c.rgb), 1.0);
}`;
